import { isNotNullable } from 'effect/Predicate'
import { useEffect } from 'react'

const disableOnClickOutsideAttributeName = 'data-disable-on-click-outside'

// この props を付けた要素 (とその子孫) のクリックは、どの useOnClickOutside でも外側クリックとみなさない
export const disableOnClickOutsideProperties = { [disableOnClickOutsideAttributeName]: true }

// https://usehooks.com/useOnClickOutside/
export function useOnClickOutside(
  reference: React.RefObject<HTMLElement>,
  handler: () => void,
  ignoreClassNames?: string[],
) {
  useEffect(
    () => {
      const callback = (pathRaw: EventTarget[], target: EventTarget | null) => {
        const path = pathRaw.map((x) => (x instanceof HTMLElement ? x : undefined)).filter(isNotNullable)

        // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
        if (!reference.current || (target instanceof HTMLElement && reference.current.contains(target))) {
          return
        }

        if (path.some((element) => element.hasAttribute(disableOnClickOutsideAttributeName))) {
          return
        }

        // 属性を付けられない antd 内部の要素 (portal 先の popup 等) はクラス名で除外する
        if (
          ignoreClassNames?.some((className) =>
            path.some((element) => typeof element.className === 'string' && element.className.includes(className)),
          ) ??
          false
        ) {
          return
        }

        handler()
      }
      const handleMouseDown = (event: MouseEvent): void => {
        callback(event.composedPath(), event.target)
      }
      const handleTouchStart = (event: TouchEvent): void => {
        callback(event.composedPath(), event.target)
      }

      document.addEventListener('mousedown', handleMouseDown)
      document.addEventListener('touchstart', handleTouchStart)

      return () => {
        document.removeEventListener('mousedown', handleMouseDown)

        document.removeEventListener('touchstart', handleTouchStart)
      }
    },
    // Add ref and handler to effect dependencies
    // It's worth noting that because passed in handler is a new ...
    // ... function on every render that will cause this effect ...
    // ... callback/cleanup to run every render. It's not a big deal ...
    // ... but to optimize you can wrap handler in useCallback before ...
    // ... passing it into this hook.
    [reference, handler],
  )
}
